/**
 * Contrato del detalle de una operación de recepción.
 *
 * El backend intenta primero con `numeroRemito` y, si no hay match, hace
 * fallback a `numeroFactura`. Devuelve los items (cada uno una transacción
 * ANMAT a confirmar) + cómo se encontró + el log de cada intento.
 *
 * Este shape es distinto del `RecepcionItem` que vive dentro de los grupos
 * del listado: acá cada item viene COMPLETO con razón social, GLN, etc.
 * (el endpoint de detalle pega directo a `/api/in/transacciones-no-confirmadas`,
 * no al agrupado).
 */

import { z } from 'zod';

export const RecepcionDetalleItemSchema = z.object({
  idTransaccion: z.string().nullable(),
  idTransaccionGlobal: z.string().nullable(),
  /** ID que ANMAT le asignó a la transacción al confirmarla. Solo presente
   *  cuando la serie fue trazada (poblado via fallback fromLocal). */
  idTransacTraza: z.string().nullable().optional(),
  /** DD/MM/YYYY */
  fechaEvento: z.string().nullable(),
  /** DD/MM/YYYY HH:mm */
  fechaTransaccion: z.string().nullable(),
  glnOrigen: z.string().nullable(),
  glnDestino: z.string().nullable(),
  razonSocialOrigen: z.string().nullable(),
  razonSocialDestino: z.string().nullable(),
  gtin: z.string().nullable(),
  lote: z.string().nullable(),
  numeroSerial: z.string().nullable(),
  vencimiento: z.string().nullable(),
  idEvento: z.string().nullable(),
  descripcionEvento: z.string().nullable(),
  nombre: z.string().nullable(),
  numeroRemito: z.string().nullable(),
  numeroFactura: z.string().nullable(),
});

export const RecepcionAttemptSchema = z.object({
  by: z.enum(['remito', 'factura']),
  request: z.unknown(),
  response: z.unknown(),
  status: z.number(),
  ok: z.boolean(),
  count: z.number(),
});

/** Cada serie del intento local previo, con su estado individual. */
export const LocalTaskDetailSchema = z.object({
  id: z.number().int(),
  idTransaccion: z.string().nullable(),
  idTransacTraza: z.string().nullable().optional(),
  numeroSerial: z.string().nullable(),
  gtin: z.string().nullable(),
  lote: z.string().nullable(),
  estado: z.string(),
  errorMessage: z.string().nullable(),
  /** Request crudo (JSON string) del POST /api/in/trazar de esta serie. */
  trazaRequest: z.string().nullable().optional(),
  /** Response crudo (JSON string) que devolvió riga/ANMAT para esta serie. */
  trazaResponse: z.string().nullable().optional(),
  tracedAt: z.string().nullable(),
});

export const LocalTaskFullSchema = z.object({
  id: z.number().int(),
  estado: z.string(),
  tracedAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
  totales: z.object({
    trazado: z.number().int().min(0),
    error: z.number().int().min(0),
    pendiente: z.number().int().min(0),
    total: z.number().int().min(0),
  }),
  details: z.array(LocalTaskDetailSchema),
});

export const RecepcionDetalleResponse = z.object({
  items: z.array(RecepcionDetalleItemSchema),
  /** null si ningún intento devolvió resultados. */
  foundBy: z.enum(['remito', 'factura']).nullable(),
  attempts: z.array(RecepcionAttemptSchema),
  /** Resumen del intento local previo (anmat_task) si existe. Trae lista
   *  de series con su estado individual + error_message. */
  localTask: LocalTaskFullSchema.nullable().optional(),
  /** true cuando los `items` fueron reconstruidos desde local (anmat_task)
   *  porque ANMAT no devolvió la operación. Típico tras un intento fallido
   *  donde ANMAT deja de listar el remito pero el snapshot vive en nuestra DB. */
  fromLocal: z.boolean().optional(),
});

export type RecepcionDetalleItem = z.infer<typeof RecepcionDetalleItemSchema>;
export type RecepcionAttempt = z.infer<typeof RecepcionAttemptSchema>;
export type LocalTaskDetail = z.infer<typeof LocalTaskDetailSchema>;
export type LocalTaskFull = z.infer<typeof LocalTaskFullSchema>;
export type RecepcionDetalle = z.infer<typeof RecepcionDetalleResponse>;

/**
 * Respuesta de `POST /api/recepcion/confirmar`. Espejo del shape que devuelve
 * riga (`/api/in/trazar-pendientes`) normalizado a camelCase.
 *
 * - `status === "success"` + `totalConfirmadas === totalPendientesEncontrados`
 *   → todas las series quedaron confirmadas.
 * - Si `errores` no es null o las cantidades no coinciden, hay confirmación
 *   parcial — el operador debe revisar.
 */
export const ConfirmarIngresoResponseSchema = z.object({
  request: z.unknown(),
  response: z.unknown(),
  status: z.string(),
  remito: z.string().nullable(),
  factura: z.string().nullable(),
  totalPendientesEncontrados: z.number().int(),
  totalConfirmadas: z.number().int(),
  idsConfirmados: z.array(z.string()),
  errores: z.unknown().nullable(),
});

export type ConfirmarIngresoResponse = z.infer<typeof ConfirmarIngresoResponseSchema>;
